import React, { FC, useMemo, useCallback } from 'react';
import { BoardInviteRestrictValues } from 'app/scripts/views/organization/constants';
import classNames from 'classnames';
import { TeamTestIds } from '@trello/test-ids';
import { forTemplate } from '@trello/i18n';
import { useFeatureFlag } from '@trello/feature-flag-client';
import { MemberAvatarUnconnected } from 'app/src/components/MemberAvatar';
import { avatarsFromAvatarUrl } from '@trello/members';
import { getNonPublicIfAvailable } from 'app/src/non-public-fields-filter';
const AutoCompleteMultiBoardGuestAlert = require('app/scripts/views/organization/invite-member-autocomplete-multi-board-guest-alert');
const format = forTemplate('select_member');

enum DisabledReasonsEnum {
  JOINED = 'joined',
  INVITED = 'invited',
  BLOCKED = 'blocked',
  RESTRICT_ORG = 'org-restrict-org',
  RESTRICT_MANAGED = 'org-restrict-managed',
  RESTRICT_MANAGED_OR_ORG = 'org-restrict-managed-or-org',
}

type NonPublicFields = 'fullName' | 'initials' | 'avatarUrl';

type PrivacyResponse = { [key in NonPublicFields]?: string | null };

// This corresponds to the response of /1/search/members/,
// which differs from normal Member properties
// When the member is an email without an account,
// only active, memberType, and email are present
interface Member {
  active: boolean;
  activityBlocked?: boolean;
  avatarUrl?: string | null;
  blockInvite?: boolean;
  confirmed?: boolean;
  email: string | null;
  fullName?: string;
  hasError?: boolean;
  id?: string;
  idBoards?: string[];
  idEnterprise?: string;
  idMemberReferrer?: string;
  idOrganizations?: string[];
  initials?: string;
  isDeactivated?: boolean;
  joined?: boolean;
  memberType?: 'ghost' | 'normal';
  nonPublic?: PrivacyResponse;
  nonPublicAvailable?: boolean;
  username?: string;
  wouldBecomeBillableGuest?: boolean;
}

interface Props {
  activeMember?: boolean;
  displayDetails: boolean;
  displayOrg?: {
    availableLicenseCount?: number;
    displayName?: string;
    id: string;
    idEnterprise?: string;
    prefs?: {
      boardInviteRestrict?: BoardInviteRestrictValues;
    };
  };
  displayUsername?: boolean;
  isDesktop?: boolean;
  isWorkspaceAdmin?: boolean;
  member: Member;
  onMouseDown: (
    e: React.MouseEvent<HTMLDivElement, MouseEvent> & { target: HTMLElement },
  ) => void;
  onRemoveClick?: (e: React.MouseEvent<HTMLAnchorElement, MouseEvent>) => void;
  onShowMultiBoardGuestAlert?: (shouldShow: boolean) => void;
  selected?: boolean;
  workspaceAdmins?: string[];
}

export const MemberAutocompleteOption: FC<Props> = ({
  activeMember,
  displayDetails,
  displayOrg,
  displayUsername,
  isDesktop,
  isWorkspaceAdmin,
  member,
  onMouseDown,
  onRemoveClick,
  onShowMultiBoardGuestAlert,
  selected,
  workspaceAdmins,
}) => {
  const {
    active,
    activityBlocked,
    blockInvite,
    confirmed,
    email,
    fullName,
    hasError,
    idEnterprise,
    idOrganizations,
    isDeactivated,
    joined,
    memberType,
    username,
    wouldBecomeBillableGuest,
  } = member;

  const disabledReason = useMemo(() => {
    let orgInviteRestrict;

    if (joined) {
      if (memberType === 'ghost') {
        return DisabledReasonsEnum.INVITED;
      }
      return DisabledReasonsEnum.JOINED;
    } else if (activityBlocked || isDeactivated) {
      return DisabledReasonsEnum.BLOCKED;
    } else if (
      displayOrg &&
      (orgInviteRestrict = displayOrg?.prefs?.boardInviteRestrict)
    ) {
      // This follows the code on server when you pass
      // onlyOrgOrManagedMembers: true
      // onlyOrgMembers: true
      // onlyManagedMembers: true
      // onlyLicensedOrManagedMembers: true
      // onlyLicensedMembers: true
      // to the api for search. We were previously not showing results via
      // sending those query params. But this confused users. So we don't
      // pass those, and instead disable the member based on same criteria.
      const isInOrg = idOrganizations?.some((idOrg) => idOrg === displayOrg.id);
      const isInEnt = idEnterprise && idEnterprise === displayOrg.idEnterprise;
      if (orgInviteRestrict === BoardInviteRestrictValues.ORG) {
        return !isInOrg && DisabledReasonsEnum.RESTRICT_ORG;
      } else if (orgInviteRestrict === BoardInviteRestrictValues.MANAGED) {
        return !isInEnt && DisabledReasonsEnum.RESTRICT_MANAGED;
      } else if (
        orgInviteRestrict === BoardInviteRestrictValues.ORG_OR_MANAGED
      ) {
        return (
          !(isInOrg || isInEnt) && DisabledReasonsEnum.RESTRICT_MANAGED_OR_ORG
        );
      }
    }
  }, [
    displayOrg,
    idEnterprise,
    idOrganizations,
    isDeactivated,
    joined,
    activityBlocked,
    memberType,
  ]);

  const isClickable = useMemo(() => {
    const availableLicenseCount = displayOrg
      ? displayOrg.availableLicenseCount ?? Infinity
      : 0;

    return (
      !disabledReason &&
      !selected &&
      !(
        wouldBecomeBillableGuest &&
        Boolean(!(displayOrg ? isWorkspaceAdmin : undefined) || isDesktop)
      ) &&
      Boolean(
        (wouldBecomeBillableGuest && availableLicenseCount > 0) ||
          !wouldBecomeBillableGuest,
      )
    );
  }, [
    disabledReason,
    displayOrg,
    isDesktop,
    isWorkspaceAdmin,
    selected,
    wouldBecomeBillableGuest,
  ]);

  const title = useMemo(() => {
    let title;

    if (displayUsername) {
      title = `${fullName} (${username})`;
    } else {
      title = fullName || email;
      if (title === username) {
        // if fullName was chosen, but it is not public
        title = `@${title}`;
      }
    }
    return title ?? '';
  }, [displayUsername, email, fullName, username]);

  const isMochaWebBetterMemberSearchEnabled = useFeatureFlag(
    'mocha.web-better-member-search',
    false,
  );

  const removeClick = useCallback(
    (e: React.MouseEvent<HTMLAnchorElement, MouseEvent>) => {
      if (blockInvite) {
        onShowMultiBoardGuestAlert?.(false);
      }
      return onRemoveClick?.(e);
    },
    [blockInvite, onRemoveClick, onShowMultiBoardGuestAlert],
  );

  const mouseDown = useCallback(
    (
      e: React.MouseEvent<HTMLDivElement, MouseEvent> & { target: HTMLElement },
    ) => {
      const wasRemoveClicked = e.target.className.indexOf('icon-close') !== -1;

      // If this is a red lozenge, show the alert on click
      if (blockInvite && !wasRemoveClicked) {
        onShowMultiBoardGuestAlert?.(true);
      }
      if (!isClickable) {
        return e.preventDefault();
      } else {
        return onMouseDown?.(e);
      }
    },
    [blockInvite, isClickable, onMouseDown, onShowMultiBoardGuestAlert],
  );

  const showMultiBoardGuestAlert = useMemo(() => {
    return !onRemoveClick && wouldBecomeBillableGuest && !disabledReason;
  }, [disabledReason, wouldBecomeBillableGuest, onRemoveClick]);

  const avatarUrl = getNonPublicIfAvailable(member, 'avatarUrl');

  return (
    <div
      role="option"
      aria-selected={selected}
      className={classNames(
        'autocomplete-option',
        !isClickable && 'disabled',
        activeMember && isClickable && 'active',
        wouldBecomeBillableGuest && !disabledReason && 'has-links',
        (hasError || blockInvite) && 'has-error',
      )}
      data-test-id={TeamTestIds.TeamInviteeOption}
      title={title}
      // Using mousedown instead of click on the option allows us to
      // select the option and then have the input blur happen afterwards
      onMouseDown={mouseDown}
    >
      <div className={classNames('member-container')}>
        {displayDetails && (
          <div
            className={classNames(
              'member',
              activityBlocked && 'member-deactivated',
            )}
          >
            {/* Use unconnected version to not query for avatars we already have, since it would take longer and fill up cache */}
            <MemberAvatarUnconnected
              avatars={avatarUrl ? avatarsFromAvatarUrl(avatarUrl) : undefined}
              initials={getNonPublicIfAvailable(member, 'initials')}
              fullName={getNonPublicIfAvailable(member, 'fullName')}
              size={32}
              hoverable={isClickable}
            />
          </div>
        )}

        <div className={classNames('member-info')}>
          <div className={classNames('full-name')}>{title}</div>
          {displayOrg &&
            displayDetails &&
            idOrganizations?.includes(displayOrg.id) && (
              <span
                className={classNames('quiet sub-name')}
                style={{ marginRight: '4px' }}
              >
                {displayOrg.displayName}
              </span>
            )}
          {displayDetails && disabledReason && (
            <span className={classNames('quiet sub-name')}>
              {format(disabledReason as string)}
            </span>
          )}
          {displayDetails &&
            !disabledReason &&
            (() => {
              if (confirmed === false) {
                return (
                  <span className={classNames('quiet sub-name')}>
                    {format(
                      isMochaWebBetterMemberSearchEnabled
                        ? 'unverified-account'
                        : 'unconfirmed-user',
                    )}
                    {isMochaWebBetterMemberSearchEnabled && (
                      <span
                        className={classNames('unverified-account-learn-more')}
                        /* eslint-disable react/no-danger */
                        dangerouslySetInnerHTML={{
                          __html: format('unverified-account-learn-more', {
                            url:
                              'https://help.trello.com/article/717-adding-people-to-a-board',
                          }),
                        }}
                        {...(isMochaWebBetterMemberSearchEnabled && {
                          onMouseDown: (e) => {
                            e.preventDefault();
                            e.stopPropagation();
                          },
                        })}
                      />
                    )}
                  </span>
                );
              } else if (active === false) {
                return (
                  <span className={classNames('quiet sub-name')}>
                    {format('inactive-account')}
                  </span>
                );
              }
            })()}
        </div>

        {onRemoveClick && (
          <a
            className={classNames('icon-sm icon-close')}
            onClick={removeClick}
            href="#"
          />
        )}
      </div>

      {showMultiBoardGuestAlert && (
        <div className={classNames('multi-board-guest-autocomplete-alert')}>
          <AutoCompleteMultiBoardGuestAlert
            displayOrg={displayOrg}
            isDesktop={isDesktop}
            isWorkspaceAdmin={isWorkspaceAdmin}
            workspaceAdmins={workspaceAdmins}
          />
        </div>
      )}
    </div>
  );
};
